import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { usePmoFormView, useSubmitPmoForm } from './api.js';

const INPUT_CLASS =
  'mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        {children}
      </div>
    </div>
  );
}

/**
 * Public, token-gated PMO pre-assign form (/pm/:token). The PMO picks the project manager
 * to pre-assign; submitting records them on the onboarding record, completes the
 * "Engage PMO" step, and auto-assigns them when the PSA project is created.
 */
export function PublicPmoPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = usePmoFormView(token);
  const submitMutation = useSubmitPmoForm(token);

  const [memberId, setMemberId] = useState('');
  const [done, setDone] = useState(false);

  const view = viewQuery.data;

  useEffect(() => {
    if (view?.currentPmId) setMemberId(view.currentPmId);
  }, [view?.currentPmId]);

  if (viewQuery.isLoading) {
    return (
      <Shell>
        <p className="text-center text-sm text-slate-500">Loading…</p>
      </Shell>
    );
  }

  if (viewQuery.isError || !view) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="text-lg font-semibold text-slate-900">Form link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">
            This link is invalid or has been disabled. Please contact the onboarding team.
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-6 w-6"
            >
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="mt-3 text-lg font-semibold text-slate-900">Project manager assigned</h1>
          <p className="mt-2 text-sm text-slate-500">
            The chosen PM has been recorded on the onboarding record and will be auto-assigned when
            the PSA project is created. You can close this window.
          </p>
        </div>
      </Shell>
    );
  }

  const error =
    submitMutation.isError && submitMutation.error instanceof Error
      ? submitMutation.error.message
      : null;

  return (
    <Shell>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-brand-600">
        Catalyst onboarding — internal
      </div>
      <h1 className="text-xl font-semibold text-slate-900">
        Pre-assign a PM — {view.customerName}
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Opportunity: <span className="font-medium text-slate-700">{view.opportunityName}</span>
        {view.opportunityTypeName ? <> · Type: {view.opportunityTypeName}</> : null}
        {view.salesRepName ? <> · Sales rep: {view.salesRepName}</> : null}
      </p>

      {view.submitted && view.currentPmName ? (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {view.currentPmName} is already pre-assigned. Submitting again replaces the assignment.
        </p>
      ) : null}

      <label className="mt-6 block text-sm">
        <span className="font-medium text-slate-700">Project manager</span>
        <span className="mt-1 block text-xs font-normal text-slate-500">
          The chosen PM is recorded on the onboarding record and auto-assigned when the PSA project
          is created.
        </span>
        <select
          value={memberId}
          onChange={(e) => setMemberId(e.target.value)}
          className={INPUT_CLASS}
        >
          <option value="">Choose a project manager…</option>
          {view.projectManagers.map((pm) => (
            <option key={pm.id} value={pm.id}>
              {pm.name}
            </option>
          ))}
        </select>
      </label>

      {error ? (
        <p className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}

      <div className="mt-6 flex justify-end border-t border-slate-100 pt-4">
        <button
          type="button"
          disabled={!memberId || submitMutation.isPending}
          onClick={() =>
            submitMutation.mutate({ memberId }, { onSuccess: () => setDone(true) })
          }
          className="rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
        >
          {submitMutation.isPending ? 'Saving…' : 'Assign PM'}
        </button>
      </div>
    </Shell>
  );
}
